// WHAT THE MIDDLE SHOWS AFTER A RESEARCH PACK (busy-my-web#532 / #533).
//
// Saffet, having made a pack out of a web search inside an Ongoing:
//
//   "When a research pack is open it shows the search I just did not the research pack document
//    research."
//   "if i click the breadcrumbs back to '1 research pack' I should see the research pack in the
//    middle … if i just created a research pack and clicked the breadcrumbs back to research pack
//    this should show the research pack not the web search."
//   "when I click home I should not see this ongoing anymore in the briefing and the middle."
//
// One cause under all three. A search inside an Ongoing hands the middle to the research workspace
// (`researchOwnsMiddle`, lib/searchMode.js) for as long as the session is ACTIVE — and filing the
// results into a pack does not end the session, nor does opening the pack, nor does walking home.
// So every place the user then navigated to was drawn UNDER the results: the pack document, the
// pack list the crumb names, and home's own middle.
//
// The answer is not a new priority rule fighting `researchOwnsMiddle` — that would make searching
// from INSIDE an open pack impossible, which is a thing people do. It is that NAVIGATING SOMEWHERE
// ELSE CLOSES THE SEARCH VIEW. `research/closeChat` is exactly that gesture and already exists (the
// workspace's own back arrow): it closes the view and keeps every turn where it was said, so the
// session is still reachable — from the story's chat chip and from the pack's own steps — while the
// place the user walked to is what fills the middle.
//
// Each navigation therefore answers for itself, here, as a list of dispatches. Plain data rather
// than store calls so the rules are testable without a store or a router; the components only loop
// over what they are given.

import { isHomePath, ongoingIdFromPath } from '@/lib/searchMode'
import { isPackFilterOn } from '@/lib/packFocus'

/** The one action that takes the middle back from the results without losing them. */
export const CLOSE_SEARCH = 'research/closeChat'

/**
 * Is the search session what the screen is about right now?
 *
 * Any of the three is enough, and they are the same three `researchOwnsMiddle` and the rail read:
 * a session that has run (`active`), the focused search mode (`searchOpen`) and the armed globe
 * (`webSearchOn`). Nothing here asks whether there are TURNS: a closed session still has its turns,
 * and that is the whole point of closing rather than resetting.
 *
 * @param {{ active?: boolean, searchOpen?: boolean, webSearchOn?: boolean }} state
 * @returns {boolean}
 */
export function researchLive({ active = false, searchOpen = false, webSearchOn = false } = {}) {
  return !!active || !!searchOpen || !!webSearchOn
}

/**
 * Opening a Research Pack (busy-my-web#533) — from the card, the rail, the Briefing's "open", a
 * deep link, or the save that just created it. Whichever door was used, the pack document is what
 * the pack page is for, so the results it came out of step aside.
 *
 * Closing a pack (`packId` null) does nothing: the user is stepping back to the story, and a search
 * they left open is theirs to find as they left it.
 *
 * @param {{ packId?: string|null, live?: boolean }} where
 * @returns {Array<{ type: string, payload?: any }>} the dispatches, in order
 */
export function packOpenEffects({ packId = null, live = false } = {}) {
  if (!packId || !live) return []
  return [{ type: CLOSE_SEARCH }]
}

/**
 * ARRIVING AT A PACK, whatever is on screen (Pouria, 2026-10-01) — the router-level door into
 * `packOpenEffects`.
 *
 * #533's rule was wired in StoryView, and StoryView is exactly what is NOT mounted while a search
 * owns an Ongoing's middle: Content.vue draws the research workspace in its place. So opening the
 * pack from the Briefing's "Open ↗", from "Saved to … View", after a second save, or from the chat
 * chip's search put `?pack=` in the URL and flipped Open to Close — and nothing closed the search,
 * because the only thing listening was unmounted. The middle stayed on the results until a reload.
 * The shell (containers/navs/Content.vue) is always mounted, so it asks this after every navigation.
 *
 * An arrival is: a `?pack=` on an Ongoing's page that is new (another pack, another page, or no
 * pack before), or the very same pack's door pressed again (`repeat` — the router reports a
 * duplicated navigation for it, and a user pressing "View" on the pack they are nominally on while
 * the results cover it wants the pack). Any other navigation that merely keeps `?pack=` in the
 * query (an email opened inside the pack, say) is not an arrival, so a search the user deliberately
 * opened from inside a pack is left alone.
 *
 * @param {{ path?: string, packId?: string|null, fromPath?: string, fromPackId?: string|null,
 *           repeat?: boolean, live?: boolean }} nav
 * @returns {Array<{ type: string, payload?: any }>}
 */
export function packArrivalEffects({ path = '', packId = null, fromPath = '', fromPackId = null, repeat = false, live = false } = {}) {
  if (!packId || !ongoingIdFromPath(path)) return []
  const arrived = repeat || String(packId) !== String(fromPackId || '') || path !== fromPath
  return arrived ? packOpenEffects({ packId, live }) : []
}

// vue-router's NavigationFailureType.duplicated — a bit flag on the failure's `type`.
const DUPLICATED = 16

/** Was this navigation the SAME location pushed again (vue-router reports it as a failure)? */
export function isRepeatNavigation(failure) {
  return !!failure && (Number(failure.type) & DUPLICATED) === DUPLICATED
}

/**
 * A BREADCRUMB CLICK that means "show me that list again" (busy-my-web#532).
 *
 * "N research packs" names the ongoing's pack LIST, and the results of the search those packs were
 * filed out of were still covering it: the crumb set the filter and the middle did not change.
 * Stepping back to it closes the search view, the same way opening a pack does.
 *
 * ONLY the closing. What the crumb sets — `ongoing/setStoryFilters`, kind only, so a pack already
 * selected under it is not un-picked (busy-my-web#258) — stays where it is, in SmartBox's
 * `onBriefingTrail` beside the other levels' answers, and is pinned there by
 * tests/unit/smartBoxBriefingTrail.spec.js.
 *
 * @param {{ level?: string, live?: boolean }} seg the crumb's level, and whether a search is on
 * @returns {Array<{ type: string, payload?: any }>}
 */
export function crumbLeavesSearch({ level = '', live = false } = {}) {
  return level === 'packs' && live ? [{ type: CLOSE_SEARCH }] : []
}

/**
 * THE STORY'S POSSIBLE CONNECTIONS OPENING (busy-my-web#680).
 *
 * Saffet: "When I click 3 possible connections it shows me 3 possible Tasks". The Briefing's
 * "N Possible connections" row hands the middle to the connections table (busy-my-web#539, frame
 * 40011046:161537) — but StoryView draws that table, and StoryView is not mounted while a search
 * owns the Ongoing's middle. The crumb moved to the "3 Possible connections ✕" chip, the strip
 * showed what the connections are ("3 Tasks · View ↗"), and the middle stayed on the results, so
 * all the click seemed to do was turn three connections into three tasks.
 *
 * Opening the table is going somewhere else, so it closes the search view, the same way arriving at
 * a pack does. Closing the table closes nothing: the story comes back, and the session is still
 * behind its chat chip.
 *
 * @param {{ open?: boolean, live?: boolean }} relations whether the table has just opened, and
 *        whether a search is on
 * @returns {Array<{ type: string, payload?: any }>}
 */
export function relationsOpenEffects({ open = false, live = false } = {}) {
  return open && live ? [{ type: CLOSE_SEARCH }] : []
}

/** The crumb levels that sit AT or BELOW an open pack — a click on one of them keeps the pack. */
const PACK_DEPTHS = ['pack', 'kind', 'attachment-class', 'entity']

/**
 * A BREADCRUMB CLICK ABOVE THE PACK (busy-my-web#574).
 *
 * Saffet: "if you look at the photo you can see I am in 1 research pack but the purple colour is
 * still at Best health drinks". The pack a save has just filled is BRIEFED with nothing in the URL
 * (`briefing/briefSavedPack`, the save lands on the story, #476), and the crumb's way out of a pack
 * was only ever dropping `?pack=` — ContextPanel clears the briefed pack by watching the query, so a
 * pack that was never in it stayed briefed. The middle followed the click (the story filter is set
 * on the spot) while the trail, built off `briefing/pack`, went on ending in the pack: the chip
 * never moved, and the crumbs looked dead until something else happened to clear the pack.
 *
 * So stepping out to any level above the pack drops the briefed pack itself, whichever way it got
 * there — in the same tick as the click, not after a navigation.
 *
 * @param {{ level?: string, packBriefed?: boolean }} seg the crumb's level, and whether a pack is
 *        what the strip is briefing right now
 * @returns {Array<{ type: string, payload?: any }>}
 */
export function crumbLeavesPack({ level = '', packBriefed = false } = {}) {
  return packBriefed && !PACK_DEPTHS.includes(level) ? [{ type: 'briefing/clearPack' }] : []
}

/** The crumb levels at or above a story's pack list — walking back to one leaves the selection. */
const SELECTION_EXITS = ['home', 'root', 'packs']

/**
 * A BREADCRUMB (or the gutter's Back / Close, which press a crumb) WALKED BACK OUT OF THE PACKS
 * (busy-my-web#671). Pouria: "when we try to select some research pack if we close the compare or
 * go back we need to clear the selection as well". The ticks used to survive every step back — the
 * Work-board chips, the Briefing's "Selected", the compare card and its pending Compare pill all
 * still up on the story's bare Briefing, or over the pack list again after closing an open pack.
 *
 * So a step back to the pack list or anything above it — Home, the story's "Briefing" root, the
 * "N research packs" crumb (a `packs` level above an open pack, the research `kind` row on the
 * story) — clears the selection through the one store action every exit uses. A step back INSIDE a
 * pack (a kind under it) keeps it, and walking FORWARD down a remembered journey never reaches
 * here. The email path's crumbs have no pack selection to leave.
 *
 * @param {{ level?: string, filter?: string, kind?: string }} seg the crumb being walked back to
 * @returns {Array<{ type: string, payload?: any }>}
 */
export function crumbLeavesPackSelection({ level = '', filter = '', kind = '' } = {}) {
  if (kind === 'emails') return []
  const exits = SELECTION_EXITS.includes(level) || (level === 'kind' && filter === 'research')
  return exits ? [{ type: 'comparison/clearSelection' }] : []
}

/**
 * HOME (busy-my-web#532): "when I click home I should not see this ongoing anymore in the briefing
 * and the middle."
 *
 * Home is the Follow-ups and the day's own middle. Walking out of an Ongoing leaves behind two
 * things that keep it on screen: the search session that story was being researched in (which owns
 * home's middle the moment it is arrived at, `researchOwnsMiddle`) and the research filter set on
 * that story (which is what the middle would be narrowed to on the way back). Both are dropped on
 * arrival. The briefed story and pack are NOT dropped here — the router already scopes those to the
 * context they were briefed in (`briefing/enterContext`, busy-my-web#122).
 *
 * Only on a walk OUT OF AN ONGOING. Home is where searching normally happens, so arriving from a
 * module page, or moving around home itself, must not close a search the user is in the middle of.
 *
 * @param {{ path?: string, from?: string, live?: boolean, filters?: object|null }} nav
 * @returns {Array<{ type: string, payload?: any }>}
 */
export function homeArrivalEffects({ path = '', from = '', live = false, filters = null } = {}) {
  if (!isHomePath(path) || !ongoingIdFromPath(from)) return []
  const out = []
  if (live) out.push({ type: CLOSE_SEARCH })
  if (isPackFilterOn(filters)) out.push({ type: 'ongoing/setStoryFilters', payload: { kind: null, packId: null } })
  return out
}

export default packOpenEffects
